/**
 * Presentation formatters for plan figures. Pure, client-safe, no I/O.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * MONEY AND HOURS ARE FORMATTED AS STRINGS, NEVER PARSED TO NUMBERS
 * ──────────────────────────────────────────────────────────────────────────────
 * Every money and hours figure crosses the API boundary as a `numeric` STRING, precisely
 * so a bill rate or a 1,440-cell grid never passes through a float64 (see the header of
 * src/schemas/resource-plans.ts and src/lib/rate-card.ts). A formatter that did
 * `Number(value).toLocaleString()` would undo that at the last step, on the figure the user
 * actually reads — so these functions insert separators textually and never call `Number`.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY THIS MODULE EXISTS, AND WHAT IS STILL DUPLICATED
 * ──────────────────────────────────────────────────────────────────────────────
 * M2 grew two private copies of this — one in `PlansTable.tsx`, one in
 * `PlanSummaryStrip.tsx` — and they disagree: the table renders a negative with a hyphen,
 * the strip with a typographic minus. M4 needed a third, fourth and fifth, which is the
 * point at which a private copy stops being cheaper than a module.
 *
 * The two M2 copies are deliberately NOT migrated here: unifying them would change what
 * the strip renders for a negative figure, and a cosmetic change to M2's output does not
 * belong in a governance slice. Recorded in the M4 handoff as a follow-up. This module
 * takes the strip's semantics (the typographic minus), because that is the one a reader
 * would call correct.
 */

/** Insert thousand separators into a decimal string, textually. */
export function formatThousands(value: string): string {
  const negative = value.startsWith("-");
  const [whole = "0", frac] = (negative ? value.slice(1) : value).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const sign = negative ? "−" : "";
  return frac === undefined ? `${sign}${grouped}` : `${sign}${grouped}.${frac}`;
}

/** `"450000.00"` → `"$450,000.00"`. A negative reads `−$1,500.00`. */
export function formatPlanMoney(value: string | null | undefined): string {
  if (value == null) return "—";
  const negative = value.startsWith("-");
  return `${negative ? "−" : ""}$${formatThousands(negative ? value.slice(1) : value)}`;
}

/** `"3000.00"` → `"3,000"`. Trailing `.00` is dropped: plan cells are whole hours. */
export function formatPlanHours(value: string | null | undefined): string {
  if (value == null) return "—";
  const grouped = formatThousands(value);
  return grouped.endsWith(".00") ? grouped.slice(0, -3) : grouped;
}

/**
 * A signed figure, always carrying its sign — for a delta column, where "0" and "no
 * change" have to be distinguishable from "−120".
 */
export function formatPlanDelta(
  value: string | null | undefined,
  kind: "money" | "hours" | "points" = "hours",
): string {
  if (value == null) return "—";
  const negative = value.startsWith("-");
  const magnitude = negative ? value.slice(1) : value;
  const body =
    kind === "money"
      ? `$${formatThousands(magnitude)}`
      : kind === "points"
        ? `${formatThousands(magnitude)} pts`
        : formatPlanHours(magnitude);
  if (Number(value) === 0) return body;
  return `${negative ? "−" : "+"}${body}`;
}

/**
 * A percent string (`"12.50"`) for display. `signed` always shows the sign, for a
 * variance; `unit: "pts"` reads a margin difference as percentage points.
 */
export function formatPlanPercent(
  value: string | null | undefined,
  opts: { signed?: boolean; unit?: "%" | "pts" } = {},
): string {
  if (value == null) return "—";
  const negative = value.startsWith("-");
  const magnitude = negative ? value.slice(1) : value;
  const suffix = opts.unit === "pts" ? " pts" : "%";
  const sign = negative ? "−" : opts.signed && Number(value) !== 0 ? "+" : "";
  return `${sign}${magnitude}${suffix}`;
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** `"2027-03"` → `"Mar 2027"`. */
export function formatPlanMonth(month: string): string {
  return `${MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? month.slice(5, 7)} ${month.slice(0, 4)}`;
}
